// AuthContext.js
//
import React, { createContext, useState, useEffect } from 'react'
import * as api from '../services/api'
import { buildSafeApiErrorMetadata } from '../services/apiErrorLogging'
import { connectSocket, disconnectSocket } from '../services/socket'

export const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // Consulta el usuario autenticado al cargar la app (me), forzando cache-bypass
  const fetchUser = async ({ throwOnError = false } = {}) => {
    try {
      const timestamp = new Date().getTime()
      const userData = await api.me({ params: { t: timestamp } })
      if (!userData || typeof userData !== 'object' || !userData.id) {
        throw new Error('Invalid session response')
      }
      setUser(userData)
      return userData
    } catch (error) {
      disconnectSocket()
      setUser(null)
      if (
        error.response &&
        error.response.status !== 401 // Solo loguea otros errores
      ) {
        console.error('Error al verificar sesión:', buildSafeApiErrorMetadata(error))
      }
      if (throwOnError) throw error
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUser()
  }, [])

  // Lógica de login: usa cookies HttpOnly con withCredentials y actualiza el usuario
  const login = async (credentials) => {
    try {
      await api.login(credentials)
    } catch (error) {
      const status = error?.response?.status
      const failure = new Error('Authentication request failed')
      failure.code = status === 401 ? 'INVALID_CREDENTIALS'
        : status === 429 ? 'LOGIN_RATE_LIMITED'
          : status ? 'LOGIN_UNAVAILABLE' : 'LOGIN_CONNECTION'
      throw failure
    }
    try {
      await fetchUser({ throwOnError: true })
    } catch (_error) {
      const failure = new Error('Session verification failed')
      failure.code = 'LOGIN_SESSION'
      throw failure
    }
    connectSocket()
    return true
  }

  // Lógica de logout: elimina la cookie de sesión en el backend
  const logout = async () => {
    try {
      await api.logout()
      localStorage.removeItem('authToken')
      sessionStorage.removeItem('authToken')
      disconnectSocket()
      setUser(null)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const activateOperationalContext = async ({ clientId, productId }) => {
    try {
      await api.setOperationalContext({ clientId, productId })
      await fetchUser()
      return true
    } catch (error) {
      console.error('activateOperationalContext error:', error)
      return false
    }
  }

  const value = {
    user,
    loading,
    login,
    logout,
    activateOperationalContext,
    refreshUser: fetchUser, // Exponer función de refresco manual
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
